import { ErrorMessageConfig } from '@config/error-message.config';
import { IQueryOption } from '@services/api/index';
import Cookies from 'universal-cookie';
import { authCookiesOption } from '../../authGuard';
import { clearAllCookies } from '@helper/cookiesClear';

const handleUnauthorized = (): void => {
	localStorage.clear();
	sessionStorage.clear();
	clearAllCookies();
	window.location.replace('/login');
};

const handleBadRequest = (responseText: string): string => {
	try {
		const errorData = JSON.parse(responseText);
		if (typeof errorData === 'object' && errorData !== null) {
			return formatErrorMessages(responseText);
		} else {
			return `Bad Request: ${errorData}`;
		}
	} catch (e) {
		return `Bad Request: ${responseText}`;
	}
};

const formatErrorMessages = (responseText: string): string => {
	const errorMessages = JSON.parse(responseText) as Record<string, unknown>;
	const message = errorMessages.message;
	if (typeof message === 'string' && message.trim()) {
		return message.trim();
	}
	return Object.entries(errorMessages)
		.map(([key, value]) => `${key.toUpperCase()}: ${value}`)
		.join('<br/>');
};

const fetchWithInterceptors = async <T>(
	url: string,
	options: RequestInit,
	isTokenRequired: boolean,
	skipJsonContentType = false
): Promise<T> => {
	const cookie = new Cookies();
	// Get token from wherever you store it
	const token: string = localStorage.getItem('token') ?? sessionStorage.getItem('token') ?? cookie.get('token');
	const isFormDataBody = options.body instanceof FormData;
	const headers: Record<string, string> = {
		Accept: 'application/json',
		...(skipJsonContentType || isFormDataBody ? {} : { 'Content-Type': 'application/json' }),
		...(token && isTokenRequired && { Authorization: `Bearer ${token}` }),
	};

	const fetchOptions = {
		...options,
		headers: {
			...options.headers,
			...headers,
		},
	};

	try {
		const response = await fetch(url, fetchOptions);
		const accessToken = response?.headers?.get('x-access-token') ?? '';
		if (accessToken) {
			cookie.set('token', accessToken, { ...authCookiesOption() });
		}

		const responseText = await response.text();

		if (!response.ok) {
			if (response.status === 401) {
				handleUnauthorized();
			} else if (response.status === 400) {
				throw new Error(handleBadRequest(responseText));
			} else if (response.status !== 404) {
				throw new Error(formatErrorMessages(responseText));
			}
		}

		if (!responseText) {
			throw new Error(ErrorMessageConfig.NETWORK_ERROR);
		}

		const result = JSON.parse(responseText);

		if (!isTokenRequired) {
			return result;
		}
		return await result;
	} catch (error) {
		if (error instanceof Error) {
			throw error;
		}
		throw new Error(ErrorMessageConfig.NETWORK_ERROR);
	}
};

export const APIService = {
	// Get data api call service
	getData: <T>(url: string, queryParams?: IQueryOption, signal?: AbortSignal): Promise<T> => {
		const options = signal ? { signal } : {};
		let apiURL = url;

		if (queryParams) {
			const params = new URLSearchParams();
			for (const [key, value] of Object.entries(queryParams)) {
				if (value !== undefined) {
					params.append(key, value.toString());
				}
			}
			apiURL += '?' + params.toString();
		}

		return fetchWithInterceptors(apiURL, options, true);
	},

	// Post data api call service
	postData: <T>(url: string, data: object, option?: Record<string, string>): Promise<T> => {
		const options: RequestInit = {
			method: 'POST',
			body: JSON.stringify(data),
		};
		if (option) {
			options.headers = option;
		}
		return fetchWithInterceptors(url, options, true);
	},

	/** Multipart POST (browser sets Content-Type + boundary). Use when uploading files e.g. audio blobs. */
	postFormData: <T>(url: string, formData: FormData): Promise<T> => {
		const options: RequestInit = {
			method: 'POST',
			body: formData,
		};
		return fetchWithInterceptors(url, options, true, true);
	},

	// Put data api call service
	putData: <T>(url: string, data?: object | string | ArrayBuffer, headers?: Record<string, string>): Promise<T> => {
		let isRequiredToken = true;
		const options: RequestInit = {
			method: 'PUT',
			body: JSON.stringify(data),
		};
		if (headers) {
			options.headers = headers;
			isRequiredToken = false;
		}
		return fetchWithInterceptors(url, options, isRequiredToken);
	},

	// Patch data api call service
	patchData: <T>(url: string, data: object | string | ArrayBuffer, headers?: Record<string, string>): Promise<T> => {
		let isRequiredToken = true;
		const options: RequestInit = {
			method: 'PATCH',
			body: JSON.stringify(data),
		};
		if (headers) {
			options.headers = headers;
			isRequiredToken = false;
		}
		return fetchWithInterceptors(url, options, isRequiredToken);
	},

	// Delete data api call service
	deleteData: <T>(url: string): Promise<T> => {
		const options = {
			method: 'DELETE',
		};
		return fetchWithInterceptors(url, options, true);
	},
};
